import { apiRequest } from "../auth/api";

export interface PermissionView {
  code: string;
  module: string;
  action: string;
  scope: "own" | "kennel" | "assigned" | "global";
}

export interface RoleView {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priority: number;
  permissions: PermissionView[];
}

export interface AuditView {
  id: string;
  actorUserId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  requestId: string;
  ipFingerprint: string | null;
  userAgent: string | null;
  before: unknown;
  after: unknown;
  metadata: unknown;
  createdAt: string;
}

export interface SettingView {
  key: string;
  group: string;
  description: string | null;
  type: "string" | "number" | "boolean" | "json" | "secret_ref";
  public: boolean;
  version: number;
  value: unknown;
  secretReference: string | null;
  validation: Record<string, unknown>;
  updatedAt: string;
}

export interface FeatureFlagView {
  id: string;
  key: string;
  description: string | null;
  environment: string;
  audienceType: string;
  audienceValue: string;
  enabled: boolean;
  rolloutPercentage: number;
  rules: Record<string, unknown>;
  version: number;
  updatedAt: string;
}

export function getRoleMatrix() {
  return apiRequest<{ roles: RoleView[]; requestId: string }>("/api/v1/admin/roles");
}

export function getAuditLogs(params: URLSearchParams) {
  return apiRequest<{ items: AuditView[]; nextCursor: string | null; requestId: string }>(
    `/api/v1/admin/audit?${params.toString()}`,
  );
}

export function getAuditDetail(id: string) {
  return apiRequest<{ audit: AuditView; requestId: string }>(
    `/api/v1/admin/audit/${encodeURIComponent(id)}`,
  );
}

export function getSettings() {
  return apiRequest<{ settings: SettingView[]; requestId: string }>("/api/v1/admin/settings");
}

export function patchSetting(
  key: string,
  body: { value: unknown; expectedVersion: number; reason: string },
) {
  return apiRequest<{ setting: SettingView; requestId: string }>(
    `/api/v1/admin/settings/${encodeURIComponent(key)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function getFeatureFlags() {
  return apiRequest<{ flags: FeatureFlagView[]; requestId: string }>(
    "/api/v1/admin/feature-flags",
  );
}

export function patchFeatureFlag(
  id: string,
  body: { enabled: boolean; rolloutPercentage: number; expectedVersion: number },
) {
  return apiRequest<{ flag: FeatureFlagView; requestId: string }>(
    `/api/v1/admin/feature-flags/${encodeURIComponent(id)}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}
